<script setup lang="ts">
import { nextTick, onMounted, ref, getCurrentInstance } from 'vue';
import { createObject, deleteObject, listObjects, updateObject } from '@/base/callBackend';
import { CabTheme, uuidOf } from 'common';
import ThemeStrip from '@/views/admin/ThemeStrip.vue';
import PlusButton from '@/views/icons/PlusIcon.vue';
import GeneralFilter from '@/views/general/GeneralFilter.vue';
// noinspection ES6UnusedImports - usages in <template> are not properly detected!
import { THEME_CHANGE_EMIT, THEME_REMOVE_EMIT } from '@/base/cabConstants';

defineEmits([THEME_CHANGE_EMIT, THEME_REMOVE_EMIT]);

const themes         = ref<CabTheme[]>([]);
const filteredThemes = ref<CabTheme[]>([]);
const filterActive   = ref<boolean>(false);

onMounted(async () => {
    themes.value = await listObjects(CabTheme);
});

//=====================================================================================
async function onThemeRemove(theme: CabTheme) {
    await deleteObject(theme);
    themes.value = themes.value.filter((o) => uuidOf(o) !== uuidOf(theme));
}

async function onThemeAdd() {
    filterActive.value = false;
    const newTheme     = new CabTheme();
    await createObject(newTheme);
    themes.value = [newTheme, ...themes.value];
    await nextTick();
    getThemeStrip(makeStripRef(newTheme))?.firstEdit();
}

async function onThemeChange(theme: CabTheme) {
    const uuid = uuidOf(theme);
    await updateObject(theme);
    themes.value = themes.value.map((o) =>
        uuidOf(o) === uuid ? theme : o,
    );
}

const instance = getCurrentInstance();

function getThemeStrip(ref: string): InstanceType<typeof ThemeStrip> | undefined {
    return (instance?.proxy?.$refs[ref] as any)?.[0];
}

function makeStripRef(x: CabTheme) {
    return `ThemeStrip-${x.uuid}`;
}

</script>

<template>
    <div class="flex flex-col flex-1 mx-28 my-5">
        <div class="flex flex-row flex-1 mb-1">
            <PlusButton @click="onThemeAdd()"/>
            <div class="text-2xl flex-1 ml-2">
                Thema's
            </div>

            <GeneralFilter v-model:active="filterActive"
                           v-model:cab-list="themes"
                           v-model:cab-list-filtered="filteredThemes"
                           :id="'ThemeList'"
            />
        </div>
        <ThemeStrip
            v-for="theme in filteredThemes"
            :key="uuidOf(theme)"
            :theme="theme"
            :ref="makeStripRef(theme)"
            v-on:[THEME_REMOVE_EMIT]="onThemeRemove"
            v-on:[THEME_CHANGE_EMIT]="onThemeChange"
        />
    </div>
</template>
